import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const FinancialOffice = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [canceling, setCanceling] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  useEffect(() => {
    // Handle return from Stripe
    if (searchParams.get('success') === 'true') {
      setMessage('Payment successful! You are now enrolled.');
      setMessageType('success');
    }
    if (searchParams.get('canceled') === 'true') {
      setMessage('Payment canceled. No charge was made.');
      setMessageType('error');
    }
    fetchPaths();
  }, []);

  const fetchPaths = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/financial/paths`);
      setPaths(res.data.paths);
    } catch (err) {
      console.error('Fetch paths error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (pathId) => {
    setCheckingOut(pathId);
    try {
      const res = await axios.post(`${API_URL}/api/financial/checkout`, {
        path_id: pathId
      });
      // Redirect to Stripe Checkout
      window.location.href = res.data.url;
    } catch (err) {
      setMessage('Error starting checkout. Please try again.');
      setMessageType('error');
      setCheckingOut(null);
    }
  };

  const handleCancel = async (subscriptionId) => {
    if (!window.confirm(
      'Cancel your subscription? You will keep access until the end of your billing period.'
    )) return;

    setCanceling(subscriptionId);
    try {
      await axios.patch(
        `${API_URL}/api/financial/subscriptions/${subscriptionId}/cancel`
      );
      setMessage('Subscription canceled. Access continues until end of billing period.');
      setMessageType('success');
      fetchPaths();
    } catch (err) {
      setMessage('Error canceling subscription.');
      setMessageType('error');
    } finally {
      setCanceling(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          <button style={styles.returnBtn} onClick={() => navigate('/campus')}>
            ← Return to Campus Square
          </button>
        </div>
      </div>

      <div style={styles.main}>
        {/* Page Header */}
        <div style={styles.pageHeader}>
          <h2 style={styles.pageTitle}>💰 Financial Office</h2>
          <p style={styles.pageSubtitle}>
            Manage your subscriptions and enrollment
          </p>
        </div>

        {/* Message */}
        {message && (
          <div style={messageType === 'success' ? 
            styles.messageSuccess : styles.messageError}>
            {message}
          </div>
        )}

        {/* Test Mode Banner */}
        <div style={styles.testBanner}>
          🧪 Test Mode — Use card number 4242 4242 4242 4242, any future 
          expiry, any CVC
        </div>

        {loading && (
          <div style={styles.centered}>
            <p style={styles.loadingText}>Loading...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* Active Subscriptions */}
            {paths.filter(p => p.is_subscribed).length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  ✅ Active Subscriptions
                </h3>
                <div style={styles.cardsGrid}>
                  {paths.filter(p => p.is_subscribed).map(path => (
                    <div key={path.id} style={styles.activeCard}>
                      <div style={styles.cardHeader}>
                        <h4 style={styles.cardTitle}>{path.title}</h4>
                        <span style={path.cancel_at_period_end ? 
                          styles.badgeCanceling : styles.badgeActive}>
                          {path.cancel_at_period_end ? 
                            'Canceling' : 'Active'}
                        </span>
                      </div>
                      <div style={styles.cardDetails}>
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>Price</span>
                          <span style={styles.detailValue}>$59/month</span>
                        </div>
                        <div style={styles.detailRow}>
                          <span style={styles.detailLabel}>
                            {path.cancel_at_period_end ? 
                              'Access ends' : 'Next billing'}
                          </span>
                          <span style={styles.detailValue}>
                            {formatDate(path.current_period_end)}
                          </span>
                        </div>
                      </div>
                      {!path.cancel_at_period_end && (
                        <button
                          style={canceling === path.stripe_subscription_id ?
                            styles.cancelBtnDisabled : styles.cancelBtn}
                          onClick={() => handleCancel(path.stripe_subscription_id)}
                          disabled={canceling === path.stripe_subscription_id}
                        >
                          {canceling === path.stripe_subscription_id ?
                            'Canceling...' : 'Cancel Subscription'}
                        </button>
                      )}
                      {path.cancel_at_period_end && (
                        <p style={styles.cancelingText}>
                          Your access continues until{' '}
                          {formatDate(path.current_period_end)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available Paths */}
            {paths.filter(p => !p.is_subscribed).length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                  📚 Available Paths
                </h3>
                <div style={styles.cardsGrid}>
                  {paths.filter(p => !p.is_subscribed).map(path => (
                    <div key={path.id} style={styles.availableCard}>
                      <div style={styles.cardHeader}>
                        <h4 style={styles.cardTitle}>{path.title}</h4>
                        <span style={styles.priceTag}>$59/mo</span>
                      </div>
                      <p style={styles.cardDesc}>{path.description}</p>
                      <button
                        style={checkingOut === path.id ?
                          styles.subscribeBtnDisabled : styles.subscribeBtn}
                        onClick={() => handleSubscribe(path.id)}
                        disabled={checkingOut === path.id}
                      >
                        {checkingOut === path.id ?
                          'Redirecting to checkout...' : 'Subscribe — $59/month'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {paths.length === 0 && (
              <div style={styles.emptyState}>
                <p style={styles.emptyText}>No paths available yet.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    color: '#ffffff',
  },
  header: {
    backgroundColor: '#0f1a33',
    borderBottom: '1px solid #C49A28',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    color: '#C49A28',
    fontSize: '24px',
    fontWeight: '800',
    margin: '0',
    letterSpacing: '2px',
  },
  logoText: {
    color: '#ffffff80',
    fontSize: '14px',
    letterSpacing: '1px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  welcome: {
    color: '#ffffff',
    fontSize: '14px',
  },
  returnBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #C49A28',
    color: '#C49A28',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  main: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px',
  },
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    color: '#C49A28',
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  pageSubtitle: {
    color: '#ffffff60',
    fontSize: '14px',
    margin: '0',
  },
  messageSuccess: {
    backgroundColor: '#1E844920',
    border: '1px solid #1E8449',
    color: '#1E8449',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  messageError: {
    backgroundColor: '#ff000015',
    border: '1px solid #ff000040',
    color: '#ff6666',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  testBanner: {
    backgroundColor: '#C49A2815',
    border: '1px solid #C49A2840',
    color: '#C49A28',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '32px',
    fontSize: '13px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: '60px',
  },
  loadingText: {
    color: '#ffffff60',
    fontSize: '16px',
  },
  section: {
    marginBottom: '40px',
  },
  sectionTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: '600',
    margin: '0 0 16px 0',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '20px',
  },
  activeCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #1E844940',
    borderRadius: '12px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  availableCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '12px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0',
    flex: 1,
  },
  badgeActive: {
    backgroundColor: '#1E844920',
    color: '#1E8449',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  badgeCanceling: {
    backgroundColor: '#ff000015',
    color: '#ff6666',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  priceTag: {
    color: '#C49A28',
    fontSize: '16px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  cardDesc: {
    color: '#ffffff60',
    fontSize: '13px',
    margin: '0',
    lineHeight: '1.5',
  },
  cardDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailLabel: {
    color: '#ffffff60',
    fontSize: '13px',
  },
  detailValue: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
  },
  subscribeBtn: {
    backgroundColor: '#C49A28',
    color: '#1A2744',
    border: 'none',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    width: '100%',
  },
  subscribeBtnDisabled: {
    backgroundColor: '#C49A2860',
    color: '#1A2744',
    border: 'none',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'not-allowed',
    width: '100%',
  },
  cancelBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ff000040',
    color: '#ff6666',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  cancelBtnDisabled: {
    backgroundColor: 'transparent',
    border: '1px solid #ff000020',
    color: '#ff666660',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'not-allowed',
    width: '100%',
  },
  cancelingText: {
    color: '#ff666680',
    fontSize: '12px',
    margin: '0',
    textAlign: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px',
  },
  emptyText: {
    color: '#ffffff30',
    fontSize: '16px',
  },
};

export default FinancialOffice;